import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { createMeetingLink, createJitsiLink } from '../meetHelper'
import { ADMIN_TZ, TIMEZONES, convertTime, fmtTime, nowInTZ, dayOfWeekIST } from '../timeHelpers'
import { checkTeacherAvailability, isWithinAvailability, getTeacherAvailability, getTeacherConflictsInRange, hasConflictOnDate } from '../availabilityHelper'
import TeacherSlotPicker from './TeacherSlotPicker'

// ── FIX: join_before_host + waiting_room disabled + type 2 (scheduled) ───────
// This means:
//   - Students and teachers can join WITHOUT the host being present
//   - No "Wait for host" screen
//   - Multiple classes can run simultaneously (each gets its own meeting ID)
//   - ONE link shared by all students in the same class (not per-student)

// ── EDIT CLASS MODAL ──────────────────────────────────────────────────────────
function EditClassModal({ cls, teachers, onClose, onSaved }) {
  const [date,      setDate]      = useState(cls.class_date || '')
  const [time,      setTime]      = useState(cls.start_time || '')
  const [title,     setTitle]     = useState(cls.title || '')
  const [teacherId, setTeacherId] = useState(cls.teacher_id || '')
  const [meetLink,  setMeetLink]  = useState(cls.meet_link || '')
  const [batch,     setBatch]     = useState(cls.batch || '')
  const [busy,      setBusy]      = useState(false)
  const [err,       setErr]       = useState('')
  const [availWarning,      setAvailWarning]      = useState('')
  const [confirmedOverride, setConfirmedOverride] = useState(false)

  useEffect(() => { setAvailWarning(''); setConfirmedOverride(false) }, [teacherId, date, time])

  async function save(e, overriding = false) {
    e?.preventDefault(); setErr('')

    if (teacherId && time && !confirmedOverride && !overriding) {
      const check = await checkTeacherAvailability({ teacherId, date, startTime: time, excludeClassId: cls.id })
      if (check.conflicts.length > 0) return setErr(`⛔ ${check.reason}`)
      if (!check.withinAvailability) { setAvailWarning(check.reason); return }
    }
    if (overriding) setConfirmedOverride(true)

    setBusy(true)
    const teacher = teachers.find(t => t.id === teacherId)
    const { error } = await supabase.from('classes').update({
      title:        title.trim(),
      class_date:   date,
      start_time:   time || null,
      teacher_id:   teacherId || null,
      teacher_name: teacher?.full_name || cls.teacher_name || null,
      meet_link:    meetLink.trim() || null,
      batch:        batch.trim() || null,
    }).eq('id', cls.id)
    if (error) { setErr(error.message); setBusy(false); return }
    if (date !== cls.class_date) {
      const d = new Date(date + 'T00:00:00')
      await supabase.from('events').update({
        day: d.getDate(), month: d.getMonth()+1, year: d.getFullYear(), time: time||null,
      }).eq('class_id', cls.id)
    }
    setBusy(false)
    onSaved()
  }

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', padding:'10px 13px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'5px', marginTop:'12px' }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'var(--bg-page)', border:'1px solid rgba(30,144,255,0.3)', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'460px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div style={{ fontSize:'16px', fontWeight:800, color:'var(--fg-solid)' }}>✏️ Edit Class</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'22px', cursor:'pointer' }}>×</button>
        </div>
        <form onSubmit={save}>
          <label style={lbl}>Title</label>
          <input style={inp} type="text" value={title} onChange={e=>setTitle(e.target.value)} required />
          <label style={lbl}>Assign Teacher</label>
          <select style={inp} value={teacherId} onChange={e=>setTeacherId(e.target.value)}>
            <option value="">— No teacher —</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </select>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={lbl}>Date (IST)</label>
              <input style={inp} type="date" value={date} onChange={e=>setDate(e.target.value)} required />
            </div>
            <div>
              <label style={lbl}>Time (IST)</label>
              <input style={inp} type="time" value={time} onChange={e=>setTime(e.target.value)} />
            </div>
          </div>
          {time && date && (
            <div style={{ marginTop:'10px', padding:'10px 12px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'9px' }}>
              <div style={{ fontSize:'9px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a20))', letterSpacing:'0.1em', marginBottom:'3px' }}>SAME TIME IN OTHER ZONES</div>
              {[
                { tz:'Asia/Kolkata',    flag:'🇮🇳', label:'IST (Admin)'  },
                { tz:'Europe/London',   flag:'🇬🇧', label:'UK'           },
                { tz:'America/New_York',flag:'🇺🇸', label:'New York'     },
                { tz:'Asia/Dubai',      flag:'🇦🇪', label:'Dubai'        },
              ].map(row => (
                <div key={row.tz} style={{ display:'flex', justifyContent:'space-between', padding:'4px 6px' }}>
                  <span style={{ fontSize:'11px', color: row.tz==='Asia/Kolkata' ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a30))' }}>{row.flag} {row.label}</span>
                  <span style={{ fontSize:'11px', fontWeight:700, color: row.tz==='Asia/Kolkata' ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a60))' }}>
                    {convertTime(date, time, ADMIN_TZ, row.tz)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <label style={lbl}>Batch / Group</label>
          <input style={inp} type="text" value={batch} onChange={e=>setBatch(e.target.value)} placeholder="e.g. Batch A" />
          <label style={lbl}>Meeting Link</label>
          <input style={inp} type="url" value={meetLink} onChange={e=>setMeetLink(e.target.value)} placeholder="https://meet.google.com/..." />
          {availWarning && (
            <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(244,163,53,0.1)', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'8px', fontSize:'12px', color:'#f4a335' }}>
              ⚠ {availWarning}. <button type="button" disabled={busy} onClick={() => save(null, true)}
                style={{ background:'none', border:'none', color:'var(--fg-solid)', fontWeight:800, textDecoration:'underline', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit', fontSize:'12px', padding:0 }}>
                Save anyway →
              </button>
            </div>
          )}
          {err && <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}
          <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
            <button type="submit" disabled={busy} style={{ flex:1, padding:'12px', background:busy?'rgba(100,100,100,0.3)':'#1e90ff', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit' }}>
              {busy ? '⏳ Saving...' : '✅ Save Changes'}
            </button>
            <button type="button" onClick={onClose} style={{ padding:'12px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── EXISTING CLASSES TABLE ────────────────────────────────────────────────────
function ExistingClasses({ profile, teachers }) {
  const [classes,     setClasses]     = useState([])
  const [loading,     setLoading]     = useState(true)
  const [editCls,     setEditCls]     = useState(null)
  const [selected,    setSelected]    = useState(new Set())
  const [search,      setSearch]      = useState('')
  const [zoomLoading, setZoomLoading] = useState(null)
  const [bulkGenLoading, setBulkGenLoading] = useState(false)
  const [ok,          setOk]          = useState('')
  const [tab,         setTab]         = useState('upcoming')

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('classes').select('*').order('class_date').order('start_time')
    setClasses(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const today = new Date().toISOString().slice(0,10)

  // FIX #1: No 30-min hide filter — show ALL upcoming classes regardless of time
  // Previously classes were hidden 30 min before start, which confused everyone
  const filtered = classes
    .filter(c => tab === 'upcoming' ? c.class_date >= today : c.class_date < today)
    .filter(c => !search ||
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.teacher_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.batch?.toLowerCase().includes(search.toLowerCase())
    )

  function toggleSelect(id) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(c => c.id)))
  }

  // Shared by the per-row button and the bulk fixer below — tries Zoom,
  // falls back to Jitsi (always works), doesn't touch UI state itself.
  async function generateLinkFor(cls) {
    const result = await createMeetingLink(cls.title, cls.class_date, cls.start_time, cls.duration_minutes || 60)
    let link = result?.url
    let provider = result?.provider
    if (!link) {
      // Zoom failed — never leave the class without a join link. Jitsi
      // links need no account, but aren't cloud-recorded.
      link = createJitsiLink(cls.title, cls.class_date, cls.start_time)
      provider = 'jitsi'
    }
    const { error } = await supabase.from('classes').update({ meet_link: link }).eq('id', cls.id)
    if (error) return { link: null, provider, error: error.message }
    return { link, provider }
  }

  // One shared link per class — tries Zoom, falls back to Jitsi (always works)
  async function generateZoom(cls) {
    setZoomLoading(cls.id)
    const { link, provider, error } = await generateLinkFor(cls)
    if (link) {
      const providerLabel = provider === 'google-meet' ? '🎥 Google Meet' : provider === 'zoom' ? '🎥 Zoom' : '🎥 Jitsi (Google Meet unavailable)'
      setOk(`✓ ${providerLabel} link generated for "${cls.title}" — instant join, no host wait`)
      setTimeout(() => setOk(''), 5000)
      load()
    } else {
      alert(`Could not save the meeting link: ${error || 'unknown error'}`)
    }
    setZoomLoading(null)
  }

  // Fixes every upcoming class in the current view that's missing a link,
  // one at a time — for when several have piled up (e.g. a bulk-created
  // batch, or a stretch where Zoom link generation was failing).
  async function generateAllMissing() {
    const missing = filtered.filter(c => !c.meet_link && c.class_date >= today)
    if (!missing.length) {
      setOk('No classes in this view are missing a link.')
      setTimeout(() => setOk(''), 3000)
      return
    }
    setBulkGenLoading(true)
    let created = 0, jitsi = 0, failed = 0
    for (const [i, cls] of missing.entries()) {
      if (i > 0) await new Promise(r => setTimeout(r, 1000)) // stay under Google's rate limit
      const { link, provider } = await generateLinkFor(cls)
      if (!link) failed++
      else if (provider === 'jitsi') jitsi++
      else created++
    }
    setBulkGenLoading(false)
    setOk(`✓ Generated ${created + jitsi} of ${missing.length} missing links` +
      (jitsi ? ` (${jitsi} on Jitsi because Google Meet was unavailable — these won't be recorded)` : '') +
      (failed ? ` · ${failed} could not be saved` : ''))
    setTimeout(() => setOk(''), 6000)
    load()
  }

  // Moves every upcoming class on a Zoom or Jitsi link onto Google Meet.
  // A class's link is only replaced when a Meet link was created, so a
  // Google problem never leaves a class worse off or without a link.
  // Paced at ~1 class/second to stay under Google's rate limit.
  const [switching, setSwitching] = useState('')
  const zoomUpcoming = classes.filter(c => c.class_date >= today && /zoom\.us\/|meet\.jit\.si\//i.test(c.meet_link || ''))
  async function switchUpcomingToMeet() {
    if (!zoomUpcoming.length) return
    if (!window.confirm(`Replace the Zoom/Jitsi link on ${zoomUpcoming.length} upcoming class(es) with a new Google Meet link? Past classes and their recordings are not affected.`)) return
    let done = 0, failed = 0, lastErr = ''
    for (const [i, cls] of zoomUpcoming.entries()) {
      setSwitching(`Switching ${i + 1} of ${zoomUpcoming.length}…`)
      if (i > 0) await new Promise(r => setTimeout(r, 1000))
      const result = await createMeetingLink(cls.title, cls.class_date, cls.start_time, cls.duration_minutes || 60)
      if (result?.provider !== 'google-meet') { failed++; lastErr = 'Google Meet link could not be created'; continue }
      const { error } = await supabase.from('classes').update({ meet_link: result.url }).eq('id', cls.id)
      if (error) { failed++; lastErr = error.message } else done++
    }
    setSwitching('')
    setOk(`✓ ${done} upcoming class(es) moved to Google Meet.` + (failed ? ` ${failed} kept their previous link (${lastErr}) — run this again later.` : ''))
    setTimeout(() => setOk(''), 10000)
    load()
  }

  // Turns on automatic recording, transcript and Gemini notes for upcoming
  // classes that already have a Google Meet link (meetings created before
  // notes were enabled). Links don't change; safe to run more than once.
  const [notesBusy, setNotesBusy] = useState('')
  const meetUpcoming = classes.filter(c => c.class_date >= today && /meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/i.test(c.meet_link || ''))
  async function enableNotesForUpcoming() {
    if (!meetUpcoming.length) return
    if (!window.confirm(`Turn on automatic recording, transcript and AI notes for ${meetUpcoming.length} upcoming Google Meet class(es)? Their links stay the same.`)) return
    const { data: { session } } = await supabase.auth.getSession()
    let notes = 0, partial = 0, failed = 0, lastErr = ''
    for (const [i, cls] of meetUpcoming.entries()) {
      setNotesBusy(`Updating ${i + 1} of ${meetUpcoming.length}…`)
      if (i > 0) await new Promise(r => setTimeout(r, 1000)) // stay under Google's rate limit
      const code = cls.meet_link.match(/meet\.google\.com\/([a-z]{3}-[a-z]{4}-[a-z]{3})/i)[1]
      try {
        const res = await fetch('/api/meetings', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'configure', meetingCode: code, accessToken: session?.access_token }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) { failed++; lastErr = data.error || `HTTP ${res.status}` }
        else if (data.autoNotes) notes++
        else partial++
      } catch (e) { failed++; lastErr = e.message }
    }
    setNotesBusy('')
    setOk(`✓ AI notes on for ${notes} class(es).` +
      (partial ? ` ${partial} got recording/transcript but not AI notes — Google didn't accept notes (check Gemini is on for the host in Google Admin).` : '') +
      (failed ? ` ${failed} failed (${lastErr}) — run this again later.` : ''))
    setTimeout(() => setOk(''), 12000)
  }

  async function deleteClass(id) {
    if (!window.confirm('Delete this class?')) return
    await supabase.from('classes').update({ is_cancelled: true }).eq('id', id)
    load()
  }

  const inp = { background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'8px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a80))', outline:'none', fontFamily:'inherit' }

  return (
    <div style={{ marginTop:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px', flexWrap:'wrap', gap:'8px' }}>
        <div style={{ fontSize:'14px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>📋 Existing Classes</div>
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...inp, width:'180px' }} type="text" placeholder="🔍 Search..." value={search} onChange={e=>setSearch(e.target.value)} />
          <button onClick={()=>setTab('upcoming')} style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', background:tab==='upcoming'?'#1e90ff':'rgba(var(--fg-rgb),0.06)', color:tab==='upcoming'?'#fff':'rgba(var(--fg-rgb),var(--a45))' }}>Upcoming</button>
          <button onClick={()=>setTab('past')}     style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', background:tab==='past'?'#1e90ff':'rgba(var(--fg-rgb),0.06)',    color:tab==='past'?'#fff':'rgba(var(--fg-rgb),var(--a45))' }}>Past</button>
          {tab === 'upcoming' && (() => {
            const missingCount = filtered.filter(c => !c.meet_link && c.class_date >= today).length
            return missingCount > 0 ? (
              <button onClick={generateAllMissing} disabled={bulkGenLoading}
                style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'0.5px solid rgba(30,144,255,0.3)', cursor: bulkGenLoading ? 'default' : 'pointer', fontFamily:'inherit', background:'rgba(30,144,255,0.15)', color:'#5aabff', opacity: bulkGenLoading ? 0.6 : 1 }}>
                {bulkGenLoading ? '⏳ Generating...' : `⚡ Generate All Missing Links (${missingCount})`}
              </button>
            ) : null
          })()}
          {tab === 'upcoming' && profile?.role === 'admin' && meetUpcoming.length > 0 && (
            <button onClick={enableNotesForUpcoming} disabled={!!notesBusy}
              style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'0.5px solid rgba(244,163,53,0.3)', cursor: notesBusy ? 'default' : 'pointer', fontFamily:'inherit', background:'rgba(244,163,53,0.12)', color:'#f4a335', opacity: notesBusy ? 0.7 : 1 }}>
              {notesBusy || `📝 Turn on AI notes for ${meetUpcoming.length} Meet class(es)`}
            </button>
          )}
          {tab === 'upcoming' && profile?.role === 'admin' && zoomUpcoming.length > 0 && (
            <button onClick={switchUpcomingToMeet} disabled={!!switching}
              style={{ fontSize:'12px', fontWeight:700, padding:'7px 14px', borderRadius:'9px', border:'0.5px solid rgba(16,185,129,0.3)', cursor: switching ? 'default' : 'pointer', fontFamily:'inherit', background:'rgba(16,185,129,0.15)', color:'#34d399', opacity: switching ? 0.7 : 1 }}>
              {switching || `🔁 Switch ${zoomUpcoming.length} Zoom/Jitsi class(es) to Google Meet`}
            </button>
          )}
        </div>
      </div>

      {ok && <div style={{ padding:'9px 14px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.25)', borderRadius:'9px', fontSize:'13px', color:'#34d399', marginBottom:'10px' }}>{ok}</div>}

      {/* Info bar */}
      <div style={{ padding:'8px 14px', background:'rgba(30,144,255,0.06)', border:'0.5px solid rgba(30,144,255,0.15)', borderRadius:'9px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'12px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
        <span>🇮🇳 All times in <strong style={{ color:'#f4a335' }}>IST</strong> · Current: <strong style={{ color:'#f4a335' }}>{fmtTime(nowInTZ(ADMIN_TZ))} IST</strong></span>
        <span style={{ color:'#34d399', fontWeight:700 }}>⚡ Google Meet: students join directly, no waiting to be let in</span>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No classes found</div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'700px' }}>
            <thead>
              <tr style={{ background:'rgba(var(--fg-rgb),0.03)' }}>
                <th style={{ padding:'9px 12px', textAlign:'left' }}>
                  <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} style={{ cursor:'pointer' }} />
                </th>
                {['Title','Date (IST)','Time (IST)','Teacher','Class Link','Actions'].map(h => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(cls => {
                const isPast = cls.class_date < today
                // FIX #1: Show class status clearly but NEVER hide upcoming classes
                const isToday = cls.class_date === today
                return (
                  <tr key={cls.id} style={{ borderTop:'0.5px solid rgba(var(--fg-rgb),0.04)', opacity: isPast ? 0.6 : 1 }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(var(--fg-rgb),0.02)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 12px' }}>
                      <input type="checkbox" checked={selected.has(cls.id)} onChange={()=>toggleSelect(cls.id)} style={{ cursor:'pointer' }} />
                    </td>
                    <td style={{ padding:'10px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:600, maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {cls.is_emergency && <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 5px', borderRadius:'4px', background:'rgba(239,68,68,0.2)', color:'#f87171', marginRight:'5px' }}>🚨</span>}
                      {cls.title}
                    </td>
                    <td style={{ padding:'10px 12px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', whiteSpace:'nowrap' }}>
                      <div style={{ fontWeight:600 }}>{new Date(cls.class_date+'T00:00:00').toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                      {isToday && <div style={{ fontSize:'9px', color:'#f87171', fontWeight:700 }}>TODAY</div>}
                    </td>
                    <td style={{ padding:'10px 12px', fontSize:'12px', color:'#f4a335', fontWeight:700, whiteSpace:'nowrap' }}>
                      {cls.start_time ? fmtTime(cls.start_time) : <span style={{ color:'rgba(var(--fg-rgb),var(--a20))' }}>—</span>}
                      {cls.start_time && <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a25))', fontWeight:400 }}>IST</div>}
                    </td>
                    <td style={{ padding:'10px 12px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a60))', whiteSpace:'nowrap' }}>
                      {cls.teacher_name || <span style={{ color:'rgba(var(--fg-rgb),var(--a20))' }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                      {cls.meet_link ? (
                        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                          <a href={cls.meet_link} target="_blank" rel="noreferrer" style={{ fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.25)', textDecoration:'none' }}>🎥 Join</a>
                          {/* Allow regenerating link */}
                          <button onClick={() => generateZoom(cls)} disabled={zoomLoading === cls.id}
                            style={{ fontSize:'10px', fontWeight:700, padding:'4px 8px', borderRadius:'7px', background:'rgba(var(--fg-rgb),0.05)', color:'rgba(var(--fg-rgb),var(--a30))', border:'0.5px solid rgba(var(--fg-rgb),0.1)', cursor:'pointer', fontFamily:'inherit' }}>
                            {zoomLoading === cls.id ? '⏳' : '↻'}
                          </button>
                        </div>
                      ) : !isPast ? (
                        <button onClick={() => generateZoom(cls)} disabled={zoomLoading === cls.id}
                          style={{ fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.3)', cursor:'pointer', fontFamily:'inherit' }}>
                          {zoomLoading === cls.id ? '⏳' : '⚡ Gen Link'}
                        </button>
                      ) : <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a20))' }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 12px', whiteSpace:'nowrap' }}>
                      <div style={{ display:'flex', gap:'5px' }}>
                        <button onClick={() => setEditCls(cls)} style={{ fontSize:'11px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', background:'rgba(30,144,255,0.12)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.2)', cursor:'pointer', fontFamily:'inherit' }}>✏️</button>
                        <button onClick={() => deleteClass(cls.id)} style={{ fontSize:'11px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', background:'rgba(239,68,68,0.1)', color:'#f87171', border:'none', cursor:'pointer', fontFamily:'inherit' }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editCls && <EditClassModal cls={editCls} teachers={teachers} onClose={() => setEditCls(null)} onSaved={() => { setEditCls(null); load(); setOk('✓ Class updated!'); setTimeout(()=>setOk(''),3000) }} />}
    </div>
  )
}

// ── MAIN SCHEDULE CLASSES ─────────────────────────────────────────────────────
export default function ScheduleClasses({ profile }) {
  const [students,   setStudents]   = useState([])
  const [teachers,   setTeachers]   = useState([])
  const [courses,    setCourses]    = useState([])
  const [busy,       setBusy]       = useState(false)
  const [ok,         setOk]         = useState('')
  const [err,        setErr]        = useState('')
  const [preview,    setPreview]    = useState([])
  const [mode,       setMode]       = useState('single')
  const [zoomStatus, setZoomStatus] = useState('')
  const [linkProgress, setLinkProgress] = useState('')

  const [selStudent,    setSelStudent]    = useState('')
  const [selTeacher,    setSelTeacher]    = useState('')
  const [selCourse,     setSelCourse]     = useState('')
  const [title,         setTitle]         = useState('')
  const [startDate,     setStartDate]     = useState('')
  const [startTime,     setStartTime]     = useState('')
  const [previewTime,   setPreviewTime]   = useState('') // debounced for world time preview
  const [zoomLink,      setZoomLink]      = useState('')
  const [autoZoom,      setAutoZoom]      = useState(true)
  const [batch,         setBatch]         = useState('')
  const [totalClasses,  setTotalClasses]  = useState(72)
  const [weekDays,      setWeekDays]      = useState([1])
  const [duration,      setDuration]      = useState(60)

  const [availWarning,      setAvailWarning]      = useState('') // outside-hours — needs override click
  const [confirmedOverride, setConfirmedOverride] = useState(false)
  const [teacherAvailRows,  setTeacherAvailRows]  = useState([]) // for the inline per-weekday hint in bulk mode

  // Debounce time input — only update preview 600ms after user stops typing
  // This fixes the 3-4 second UI freeze on every keystroke
  const timeDebounceRef = useRef(null)
  function handleTimeChange(val) {
    setStartTime(val)
    clearTimeout(timeDebounceRef.current)
    timeDebounceRef.current = setTimeout(() => setPreviewTime(val), 600)
  }

  useEffect(() => { loadData() }, [])

  // Any change to who/when resets a prior override — re-check next submit.
  useEffect(() => { setAvailWarning(''); setConfirmedOverride(false) }, [selTeacher, startDate, startTime, duration, mode, weekDays, totalClasses])

  // For the bulk-mode inline weekday hint — no DB round-trip per toggle.
  useEffect(() => {
    if (!selTeacher) { setTeacherAvailRows([]); return }
    getTeacherAvailability(selTeacher).then(setTeacherAvailRows)
  }, [selTeacher])

  async function loadData() {
    const [{ data: s }, { data: t }, { data: c }] = await Promise.all([
      supabase.from('profiles').select('id,full_name,email,timezone').eq('role','student').order('full_name'),
      supabase.from('profiles').select('id,full_name').eq('role','teacher').order('full_name'),
      supabase.from('courses').select('id,title,duration_months').order('title'),
    ])
    setStudents(s || [])
    setTeachers(t || [])
    setCourses(c || [])
    if (profile.role === 'teacher') setSelTeacher(profile.id)
  }

  const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

  function toggleDay(d) {
    setWeekDays(prev => prev.includes(d) ? prev.filter(x=>x!==d) : [...prev,d].sort())
  }

  function localDateStr(d) {
    return d.getFullYear() + '-' +
      String(d.getMonth()+1).padStart(2,'0') + '-' +
      String(d.getDate()).padStart(2,'0')
  }

  function generateDates(start, days, total) {
    if (!start || !days.length || !total) return []
    const dates = []
    const current = new Date(start + 'T00:00:00')
    while (dates.length < total) {
      if (days.includes(current.getDay())) dates.push(localDateStr(current))
      current.setDate(current.getDate() + 1)
      if (current.getFullYear() > new Date().getFullYear() + 10) break
    }
    return dates.slice(0, total)
  }

  useEffect(() => {
    if (mode === 'bulk' && startDate) {
      const dayOfWeek = new Date(startDate + 'T00:00:00').getDay()
      setWeekDays([dayOfWeek])
    }
  }, [startDate, mode])

  useEffect(() => {
    if (mode === 'bulk') setPreview(generateDates(startDate, weekDays, totalClasses))
    else setPreview([])
  }, [startDate, weekDays, totalClasses, mode])

  const selectedStudent = students.find(s => s.id === selStudent)
  const studentTZ = selectedStudent?.timezone || null

  async function scheduleClasses(e, overriding = false) {
    e?.preventDefault()
    setErr(''); setOk(''); setBusy(true); setZoomStatus('')

    if (!title.trim())  { setErr('Enter class title.'); setBusy(false); return }
    if (!startDate)     { setErr('Select start date.'); setBusy(false); return }
    if (mode==='bulk' && !weekDays.length) { setErr('Select at least one day.'); setBusy(false); return }

    // Mandatory — a class can't be created without a student enrolled (per
    // user request 2026-09-05, previously an easy-to-miss confirm() bypass).
    if (!selStudent) { setErr('Select a student — a class cannot be scheduled with nobody enrolled.'); setBusy(false); return }

    const teacher = teachers.find(t => t.id === selTeacher)
    const student = students.find(s => s.id === selStudent)
    const course  = courses.find(c => c.id === selCourse)
    const dates   = mode === 'bulk' ? generateDates(startDate, weekDays, totalClasses) : [startDate]

    // Availability check — real class conflicts always hard-block; being
    // outside the teacher's declared hours only warns, with one more
    // click to proceed (real one-off exceptions — makeup classes, extra
    // coverage — happen). Bulk mode fetches the whole date range in one
    // query rather than one round-trip per generated date.
    if (selTeacher && startTime && !confirmedOverride && !overriding) {
      const availRows    = await getTeacherAvailability(selTeacher)
      const conflictRows = await getTeacherConflictsInRange({ teacherId: selTeacher, fromDate: dates[0], toDate: dates[dates.length - 1] })
      const outsideDates  = dates.filter(d => !isWithinAvailability(availRows, dayOfWeekIST(d), startTime, duration))
      const conflictDates = dates.filter(d => hasConflictOnDate(conflictRows, d, startTime, duration))

      if (conflictDates.length) {
        setErr(`⛔ ${conflictDates.length} of ${dates.length} date${dates.length===1?'':'s'} conflict with ${teacher?.full_name || 'this teacher'}'s existing classes: ${conflictDates.slice(0,5).join(', ')}${conflictDates.length>5?'…':''}`)
        setBusy(false); return
      }
      if (outsideDates.length) {
        setAvailWarning(`${outsideDates.length} of ${dates.length} date${dates.length===1?'':'s'} fall outside ${teacher?.full_name || 'this teacher'}'s declared working hours`)
        setBusy(false); return
      }
    }
    if (overriding) setConfirmedOverride(true)

    try {
      const istTime = startTime || null
      let finalZoomLink = zoomLink.trim() || null

      // One shared link per class (single) or unique link per class (bulk)
      // Uses Zoom if available, falls back to Jitsi (always works, instant join)
      if (autoZoom && istTime && !finalZoomLink) {
        setZoomStatus('generating')
        const result = await createMeetingLink(title.trim(), dates[0], istTime, duration)
        if (result?.url) { finalZoomLink = result.url; setZoomStatus('done') }
        else { finalZoomLink = createJitsiLink(title.trim(), dates[0], istTime); setZoomStatus('error') }
      }

      const CHUNK = 50
      let inserted = []

      if (mode === 'bulk') {
        // Each class gets its own Google Meet link. Links are created one at
        // a time (~1/second): Google rate-limits bursts, and creating 50 at
        // once used to push most classes onto unrecorded Jitsi links.
        const links = []
        if (autoZoom && istTime && !zoomLink.trim()) {
          setZoomStatus('generating')
          let fallbacks = 0
          for (const [n, d] of dates.entries()) {
            setLinkProgress(`Creating Google Meet link ${n + 1} of ${dates.length}…`)
            if (n > 0) await new Promise(r => setTimeout(r, 1000))
            const r = await createMeetingLink(`${title.trim()} (${n + 1}/${dates.length})`, d, istTime, duration)
            if (!r?.url) fallbacks++
            links.push(r?.url || createJitsiLink(`${title.trim()} (${n + 1}/${dates.length})`, d, istTime))
          }
          setLinkProgress('')
          setZoomStatus(fallbacks ? 'error' : 'done')
        }
        for (let i = 0; i < dates.length; i += CHUNK) {
          const chunk = dates.slice(i, i + CHUNK)
          const classRecords = chunk.map((d, idx) => {
            const classZoomLink = links[i + idx] || finalZoomLink
            return {
              title:            `${title.trim()} (Class ${i + idx + 1}/${dates.length})`,
              teacher_id:       selTeacher || null,
              teacher_name:     teacher?.full_name || null,
              class_date:       d,
              start_time:       istTime,
              duration_minutes: duration,
              batch:            batch || course?.title || null,
              meet_link:        classZoomLink,
              created_by:       profile.full_name,
            }
          })

          const { data: chunkData, error } = await supabase.from('classes').insert(classRecords).select()
          if (error) throw new Error(error.message)
          inserted = inserted.concat(chunkData || [])
        }
      } else {
        // Single class — one shared link for all students
        const { data: singleData, error } = await supabase.from('classes').insert([{
          title:            title.trim(),
          teacher_id:       selTeacher || null,
          teacher_name:     teacher?.full_name || null,
          class_date:       startDate,
          start_time:       istTime,
          duration_minutes: duration,
          batch:            batch || course?.title || null,
          meet_link:        finalZoomLink,
          created_by:       profile.full_name,
        }]).select()
        if (error) throw new Error(error.message)
        inserted = singleData || []
      }

      // Enroll student
      if (selStudent && inserted.length) {
        for (let i = 0; i < inserted.length; i += CHUNK) {
          const { error: enrollErr } = await supabase.from('enrollments').insert(
            inserted.slice(i, i + CHUNK).map(c => ({
              student_id: selStudent,
              class_id: c.id,
              course_id: selCourse || null,
            }))
          )
          if (enrollErr) throw new Error(`Classes were created, but enrolling the student failed: ${enrollErr.message}`)
        }
      }

      // Insert events
      const eventRecords = inserted.map(c => {
        const dt = new Date(c.class_date + 'T00:00:00')
        return {
          title: c.title, event_type: 'class',
          day: dt.getDate(), month: dt.getMonth()+1, year: dt.getFullYear(),
          time: istTime, teacher_name: teacher?.full_name || null,
          batch: batch || course?.title || null,
          class_id: c.id,
        }
      })
      for (let i = 0; i < eventRecords.length; i += CHUNK) {
        await supabase.from('events').insert(eventRecords.slice(i, i + CHUNK))
      }

      // Email student
      if (selStudent && student?.email) {
        const studentTime = istTime && studentTZ ? convertTime(startDate, istTime, ADMIN_TZ, studentTZ) : istTime
        sendEmail('class_scheduled', student.email, {
          name: student.full_name, classTitle: title.trim(),
          classDate: mode==='bulk' ? `${dates[0]} + ${dates.length-1} more` : dates[0],
          startTime: studentTime, teacherName: teacher?.full_name || null,
          zoomLink: finalZoomLink, studentTZ: studentTZ || ADMIN_TZ,
        })
      }

      const meetMsg = finalZoomLink
        ? ' · ⚡ Meeting link created — instant join, no host needed!'
        : ''
      setOk(mode==='bulk'
        ? `✓ ${inserted.length} classes scheduled!${meetMsg}`
        : `✓ Class scheduled!${meetMsg}`)
      setTitle(''); setStartDate(''); setStartTime(''); setZoomLink('')
      setBatch(''); setSelStudent(''); setPreview([]); setZoomStatus('')

    } catch(e) { setErr('Error: ' + e.message) }
    setBusy(false)
    setTimeout(() => setOk(''), 8000)
  }

  const inp = { width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'11px 13px', fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }
  const sel = { ...inp, background:'var(--bg-input)' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a32))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px', marginTop:'14px' }

  return (
    <div id="uniedd-schedule-classes" style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>📅 Schedule Classes</div>

      {/* Info banner */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', padding:'10px 14px', background:'rgba(16,185,129,0.06)', border:'0.5px solid rgba(16,185,129,0.2)', borderRadius:'10px', marginBottom:'16px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))' }}>
        <span>🇮🇳 Enter times in <strong style={{ color:'#f4a335' }}>IST</strong> · Now: <strong style={{ color:'#f4a335' }}>{fmtTime(nowInTZ(ADMIN_TZ))} IST</strong></span>
        <span style={{ color:'#34d399', fontWeight:700 }}>✅ Students join Google Meet directly — no waiting to be let in</span>
        <span style={{ color:'#5aabff', fontWeight:700 }}>⚡ Multiple classes can run at the same time</span>
        <span style={{ color:'#a78bfa', fontWeight:700 }}>🔗 One shared link per class for all students</span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'20px' }}>
        {[
          { id:'single', icon:'📌', label:'Single Class',       sub:'One class at a time',          color:'#1e90ff' },
          { id:'bulk',   icon:'📆', label:'Bulk — Full Course', sub:'All classes for entire course', color:'#8b5cf6' },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} style={{ padding:'14px', borderRadius:'12px', border:`1.5px solid ${mode===m.id?m.color:'rgba(var(--fg-rgb),0.08)'}`, background:mode===m.id?`${m.color}18`:'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left' }}>
            <div style={{ fontSize:'22px', marginBottom:'6px' }}>{m.icon}</div>
            <div style={{ fontSize:'13px', fontWeight:700, color:mode===m.id?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a50))' }}>{m.label}</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px' }}>{m.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.2rem' }}>
          <form onSubmit={scheduleClasses}>
            <label style={lbl}>Class Title *</label>
            <input style={inp} type="text" placeholder="e.g. Guitar — Batch A" value={title} onChange={e=>setTitle(e.target.value)} required />

            {profile.role !== 'teacher' && (
              <>
                <label style={lbl}>Assign Teacher</label>
                <select style={sel} value={selTeacher} onChange={e=>setSelTeacher(e.target.value)}>
                  <option value="">— Select teacher —</option>
                  {teachers.map(t=><option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </>
            )}

            {mode === 'single' && selTeacher && (
              <TeacherSlotPicker teacherId={selTeacher} durationMinutes={duration} selectedDate={startDate}
                onPick={({ date, time }) => { setStartDate(date); if (time) handleTimeChange(time) }}/>
            )}

            <label style={lbl}>Assign Student *</label>
            <select style={sel} value={selStudent} onChange={e=>setSelStudent(e.target.value)}>
              <option value="">— Select student (required) —</option>
              {students.map(s=><option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>

            <label style={lbl}>Course (optional)</label>
            <select style={sel} value={selCourse} onChange={e=>setSelCourse(e.target.value)}>
              <option value="">— Link to course —</option>
              {courses.map(c=><option key={c.id} value={c.id}>{c.title}</option>)}
            </select>

            <div style={{ marginTop:'14px', padding:'14px', background:'rgba(244,163,53,0.05)', border:'0.5px solid rgba(244,163,53,0.15)', borderRadius:'12px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#f4a335', letterSpacing:'0.08em', marginBottom:'10px' }}>🇮🇳 DATE & TIME (IST)</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div>
                  <label style={{ ...lbl, marginTop:0 }}>{mode==='bulk'?'Start Date':'Date'} *</label>
                  <input style={inp} type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} required />
                </div>
                <div>
                  <label style={{ ...lbl, marginTop:0 }}>Time (IST)</label>
                  <input style={inp} type="time" value={startTime} onChange={e=>handleTimeChange(e.target.value)} />
                </div>
              </div>
              {/* World time preview — debounced, only updates 600ms after typing stops */}
              {previewTime && startDate && (
                <div style={{ marginTop:'10px', display:'grid', gap:'3px' }}>
                  {[
                    { tz:'Asia/Kolkata',    flag:'🇮🇳', label:'IST (India)'   },
                    { tz:'Asia/Dubai',      flag:'🇦🇪', label:'Dubai (GST)'   },
                    { tz:'Europe/London',   flag:'🇬🇧', label:'UK'            },
                    { tz:'America/New_York',flag:'🇺🇸', label:'New York'      },
                    { tz:'Asia/Singapore',  flag:'🇸🇬', label:'Singapore'     },
                  ].map(row => (
                    <div key={row.tz} style={{ display:'flex', justifyContent:'space-between', padding:'3px 6px', borderRadius:'5px', background: row.tz==='Asia/Kolkata'?'rgba(244,163,53,0.08)':'transparent' }}>
                      <span style={{ fontSize:'11px', color: row.tz==='Asia/Kolkata' ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a30))' }}>{row.flag} {row.label}</span>
                      <span style={{ fontSize:'11px', fontWeight:700, color: row.tz==='Asia/Kolkata' ? '#f4a335' : 'rgba(var(--fg-rgb),var(--a65))' }}>
                        {convertTime(startDate, previewTime, ADMIN_TZ, row.tz)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label style={lbl}>Duration</label>
            <select style={sel} value={duration} onChange={e=>setDuration(parseInt(e.target.value))}>
              {[30,45,60,90,120].map(d=><option key={d} value={d}>{d} minutes</option>)}
            </select>

            {/* Zoom settings */}
            <div style={{ marginTop:'14px', padding:'14px', background:'rgba(16,185,129,0.06)', border:'0.5px solid rgba(16,185,129,0.2)', borderRadius:'12px' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:'#34d399', marginBottom:'8px' }}>🎥 Meeting Link</div>
              <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'10px', lineHeight:1.5 }}>
                ✅ <strong style={{ color:'#34d399' }}>join_before_host ON</strong> — students & teacher join instantly<br/>
                ✅ <strong style={{ color:'#34d399' }}>No waiting room</strong> — no "Wait for host" screen<br/>
                ✅ <strong style={{ color:'#34d399' }}>Unique meeting per class</strong> — multiple classes run simultaneously<br/>
                ✅ <strong style={{ color:'#34d399' }}>One shared link</strong> — all students in a class use the same link
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a70))', marginBottom:'10px' }}>
                <input type="checkbox" checked={autoZoom} onChange={e=>setAutoZoom(e.target.checked)} />
                ⚡ Auto-generate Zoom when scheduling
              </label>
              {zoomStatus === 'generating' && <div style={{ fontSize:'12px', color:'#5aabff' }}>⏳ {linkProgress || 'Generating Google Meet link...'} Keep this page open.</div>}
              {zoomStatus === 'done'       && <div style={{ fontSize:'12px', color:'#34d399' }}>✓ Google Meet link generated!</div>}
              {zoomStatus === 'error'      && <div style={{ fontSize:'12px', color:'#f4a335' }}>⚠ Google Meet failed — used a Jitsi link instead (no cloud recording on Jitsi)</div>}
              <label style={{ ...lbl, marginTop:'8px' }}>Or paste a manual link</label>
              <input style={inp} type="url" placeholder="https://meet.google.com/... (optional)" value={zoomLink} onChange={e=>setZoomLink(e.target.value)} />
            </div>

            <label style={lbl}>Batch / Group (optional)</label>
            <input style={inp} type="text" placeholder="e.g. Batch A, Beginners" value={batch} onChange={e=>setBatch(e.target.value)} />

            {mode === 'bulk' && (
              <div style={{ marginTop:'14px', padding:'14px', background:'rgba(139,92,246,0.08)', border:'0.5px solid rgba(139,92,246,0.2)', borderRadius:'12px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:'#a78bfa', marginBottom:'12px' }}>📆 BULK SCHEDULE SETTINGS</div>

                <label style={{ ...lbl, marginTop:0 }}>Total Classes</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'6px', marginBottom:'8px' }}>
                  {[24,36,48,60,72,84,96,120].map(n=>(
                    <button key={n} type="button" onClick={()=>setTotalClasses(n)} style={{ padding:'8px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:700, background:totalClasses===n?'#8b5cf6':'rgba(var(--fg-rgb),0.05)', color:totalClasses===n?'#fff':'rgba(var(--fg-rgb),var(--a50))', fontFamily:'inherit' }}>{n}</button>
                  ))}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                  <input type="number" min="1" max="500" value={totalClasses}
                    onChange={e => setTotalClasses(Math.max(1, Math.min(500, parseInt(e.target.value)||1)))}
                    style={{ ...inp, width:'100px', padding:'8px 12px', fontSize:'14px', fontWeight:700, textAlign:'center' }}
                  />
                  <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>Enter any number (1–500)</span>
                </div>

                <label style={{ ...lbl, marginTop:0 }}>
                  Repeat on days
                  {startDate && <span style={{ fontSize:'10px', color:'#a78bfa', fontWeight:400, marginLeft:'8px' }}>(auto-set from start date)</span>}
                </label>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'10px' }}>
                  {DAY_NAMES.map((d,i)=>{
                    const outsideHours = selTeacher && startTime && teacherAvailRows.length > 0 && !isWithinAvailability(teacherAvailRows, i, startTime, duration)
                    return (
                      <button key={i} type="button" onClick={()=>toggleDay(i)} title={outsideHours ? `Outside ${teachers.find(t=>t.id===selTeacher)?.full_name || 'teacher'}'s hours` : undefined}
                        style={{ position:'relative', width:'40px', height:'40px', borderRadius:'50%', border: weekDays.includes(i) ? '2px solid #8b5cf6' : outsideHours ? '1.5px solid rgba(244,163,53,0.5)' : 'none', cursor:'pointer', fontSize:'12px', fontWeight:700, background:weekDays.includes(i)?'#8b5cf6':'rgba(var(--fg-rgb),0.07)', color:weekDays.includes(i)?'#fff':'rgba(var(--fg-rgb),var(--a40))', fontFamily:'inherit' }}>
                        {d}
                        {outsideHours && <span style={{ position:'absolute', top:'-3px', right:'-3px', fontSize:'10px' }}>⚠</span>}
                      </button>
                    )
                  })}
                </div>

                {preview.length > 0 && (
                  <div style={{ padding:'10px 12px', background:'rgba(139,92,246,0.1)', borderRadius:'8px', fontSize:'12px' }}>
                    <span style={{ color:'#a78bfa', fontWeight:700 }}>{preview.length} classes</span>
                    <span style={{ color:'rgba(var(--fg-rgb),var(--a40))' }}> · {preview[0] ? new Date(preview[0]+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : ''}</span>
                    <span style={{ color:'rgba(var(--fg-rgb),var(--a30))' }}> → </span>
                    <span style={{ color:'rgba(var(--fg-rgb),var(--a40))' }}>{preview[preview.length-1] ? new Date(preview[preview.length-1]+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : ''}</span>
                    <div style={{ color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'4px', fontSize:'11px' }}>
                      Every {weekDays.map(d=>DAY_NAMES[d]).join(' & ')} {startTime && `at ${fmtTime(startTime)} IST`}
                    </div>
                    <div style={{ color:'#a78bfa', marginTop:'4px', fontSize:'11px', fontWeight:600 }}>
                      ⚡ Each class gets its own unique Zoom meeting — all can run simultaneously
                    </div>
                  </div>
                )}
              </div>
            )}

            {availWarning && (
              <div style={{ marginTop:'10px', padding:'10px 13px', background:'rgba(244,163,53,0.1)', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'8px', fontSize:'12px', color:'#f4a335' }}>
                ⚠ {availWarning}. <button type="button" disabled={busy} onClick={() => scheduleClasses(null, true)}
                  style={{ background:'none', border:'none', color:'var(--fg-solid)', fontWeight:800, textDecoration:'underline', cursor:busy?'not-allowed':'pointer', fontFamily:'inherit', fontSize:'12px', padding:0 }}>
                  Schedule anyway →
                </button>
              </div>
            )}
            {err && <div style={{ marginTop:'10px', padding:'9px 13px', background:'rgba(220,60,60,0.1)', border:'0.5px solid rgba(220,60,60,0.3)', borderRadius:'8px', fontSize:'13px', color:'#f09595' }}>{err}</div>}
            {ok  && <div style={{ marginTop:'10px', padding:'9px 13px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', fontSize:'13px', color:'#34d399' }}>{ok}</div>}

            <button type="submit" disabled={busy} style={{ width:'100%', padding:'13px', background:busy?'rgba(100,100,100,0.3)':mode==='bulk'?'linear-gradient(135deg,#8b5cf6,#6d28d9)':'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'14px', fontWeight:800, border:'none', borderRadius:'10px', cursor:busy?'not-allowed':'pointer', marginTop:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
              {busy
                ? <><span style={{ display:'inline-block',width:'14px',height:'14px',border:'2px solid rgba(var(--fg-rgb),var(--a30))',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite'}}/> {linkProgress || 'Scheduling...'}</>
                : mode==='bulk' ? `📆 Schedule All ${preview.length||totalClasses} Classes` : '📌 Schedule Class'
              }
            </button>
          </form>
        </div>

        {/* Preview panel */}
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.2rem' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))', marginBottom:'12px' }}>
            {mode==='bulk' ? `📋 Preview — ${preview.length} Classes` : '📋 Summary'}
          </div>
          {mode === 'single' ? (
            <div style={{ display:'grid', gap:'8px' }}>
              {[
                { label:'Title',     value: title || '—' },
                { label:'Teacher',   value: teachers.find(t=>t.id===selTeacher)?.full_name || '—' },
                { label:'Student',   value: students.find(s=>s.id===selStudent)?.full_name || 'Not assigned' },
                { label:'Date',      value: startDate || '—' },
                { label:'Time (IST)',value: startTime ? fmtTime(startTime) : '—' },
                { label:'Duration',  value: `${duration} min` },
                { label:'Link',      value: zoomLink ? '✓ Manual link' : autoZoom ? '⚡ Auto-generate on save' : 'None' },
                { label:'Join mode', value: '✅ Instant — no wait for host' },
              ].map(row=>(
                <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'8px' }}>
                  <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{row.label}</span>
                  <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a80))', fontWeight:500 }}>{row.value}</span>
                </div>
              ))}
            </div>
          ) : preview.length === 0 ? (
            <div style={{ textAlign:'center', padding:'3rem 1rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Select start date and days to preview</div>
          ) : (
            <div style={{ maxHeight:'400px', overflowY:'auto', display:'grid', gap:'3px' }}>
              {preview.map((d,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 10px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'7px' }}>
                  <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'6px', background:'rgba(139,92,246,0.15)', color:'#a78bfa', flexShrink:0 }}>#{i+1}</span>
                  <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', flex:1 }}>{new Date(d + 'T00:00:00').toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short',year:'numeric'})}</span>
                  {startTime && <span style={{ fontSize:'11px', color:'#f4a335' }}>{fmtTime(startTime)} IST</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ExistingClasses profile={profile} teachers={teachers} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
